import { useEffect, useRef, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Estado, data, useAcao, useAtualizacaoPeriodica, useCarga } from '../comum';
import { Briefing } from './Briefing';
import { Centro } from './Centro';
import { EditorCartao } from './EditorCartao';
import { GerenciarPaineis } from './GerenciarPaineis';
import { Tendencias } from './Tendencias';
import { CartaoPainel, comFiltroDeGrupo } from './inicio/cartoes';
import {
  CORES,
  ROTULOS,
  ordenar,
  rotular,
  type Cartao,
  type DadosCartao,
  type FormaCartao,
  type IndicadorDisponivel,
  type Pendencia,
  type RespostaInicial,
  type RespostaPendencias,
  type Variacao,
} from './inicio/tipos';

/* Os contratos do painel seguem publicados por esta tela — é daqui que o
   editor de cartão e a aba Perguntar os consomem. */
export type { Cartao, FormaCartao };

/**
 * Tela inicial.
 *
 * O painel é montado sobre a mesma consulta estruturada que o chat produz —
 * então "salvar como cartão" na aba Perguntar cria um cartão aqui, sem ninguém
 * aprender linguagem de relatório.
 */
export function Inicio({
  aoPerderSessao,
  irPara,
  podeSincronizar,
  aoAbrirNoInventario,
  meuLogin,
}: {
  aoPerderSessao: () => void;
  irPara: (
    aba: 'inventario' | 'chat' | 'sentinela' | 'tickets' | 'aprovacoes' | 'execucoes' | 'problemas',
  ) => void;
  podeSincronizar: boolean;
  /** Abre o Inventário já filtrado pela consulta do cartão. */
  aoAbrirNoInventario: (f: { titulo: string; explicacao: string; consulta: unknown }) => void;
  /** Login da sessão — distingue painel próprio de painel compartilhado. */
  meuLogin: string;
}): ReactNode {
  const carga = useCarga<RespostaInicial>(
    () => api.get('/api/paineis/inicial'),
    [],
    aoPerderSessao,
  );
  const pend = useCarga<RespostaPendencias>(
    () => api.get('/api/inicio/pendencias'),
    [],
    aoPerderSessao,
  );
  const acao = useAcao(aoPerderSessao);
  const [salvo, setSalvo] = useState(false);
  /** Id do cartão sendo arrastado. `null` = ninguém. */
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [editando, setEditando] = useState<Cartao | null>(null);
  const [gerindo, setGerindo] = useState(false);
  const [escolhendoIndicador, setEscolhendoIndicador] = useState(false);

  /**
   * 🔴 **O editor abria fora do campo de visão, e o ✎ parecia botão quebrado.**
   *
   * ───────────────────────────────────────────────────────────────────────────
   * **Relatado em 24/09/2026:** *"quando clico em ✎ não está abrindo a página
   * de edição"*. Ele abria. `.editor-cartao` é uma caixa comum da página — sem
   * `position: fixed`, sem sobreposição — e renderiza **acima da grade de
   * cartões**. Quem clica no ✎ do último cartão de um painel com seis recebe o
   * editor a centenas de pixels para cima, e nada leva a página até lá.
   *
   * ⚠️ **É o mesmo defeito que `Configuracoes.tsx` já documenta** para o aviso
   * de erro — *"o aviso renderizado aqui em cima nunca entrava no campo de
   * visão de quem acabara de clicar"*. Lá foi resolvido tornando o aviso
   * flutuante; aqui o editor precisa continuar no fluxo da página (ele é
   * grande, tem abas e um construtor de filtros dentro), então quem se move é
   * a página.
   *
   * ⚠️ A rolagem respeita `prefers-reduced-motion`: movimento involuntário de
   * página inteira é justamente o que essa preferência existe para evitar.
   * ───────────────────────────────────────────────────────────────────────────
   */
  const refEditor = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (editando === null) return;
    const alvo = refEditor.current;
    if (alvo === null) return;

    const suave = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    alvo.scrollIntoView({ behavior: suave ? 'smooth' : 'auto', block: 'start' });
  }, [editando]);

  const catalogo = useCarga<{ itens: readonly IndicadorDisponivel[] }>(
    () => api.get('/api/paineis/indicadores'),
    [],
    aoPerderSessao,
  );

  /** Acrescenta um cartão de indicador ao painel do usuário. */
  const acrescentarIndicador = (d: IndicadorDisponivel): void => {
    const p = carga.dados?.painel;
    if (p === undefined || p.id === null) {
      void acao.rodar(async () => {
        throw new Error(
          'Este é o painel padrão. Clique em "Personalizar este painel" antes de acrescentar cartões.',
        );
      });
      return;
    }
    void acao.rodar(async () => {
      await api.post(`/api/paineis/${p.id}/cartoes`, {
        titulo: d.titulo,
        forma: 'numero',
        largura: 1,
        limiteAlerta: null,
        nota: d.nota,
        indicador: d.chave,
        consulta: { intencao: 'contar', filtros: [], agruparPor: null, explicacao: d.titulo },
      });
      setEscolhendoIndicador(false);
      carga.recarregar();
      return `Cartão "${d.titulo}" acrescentado.`;
    });
  };

  const sincronizar = (): void => {
    void acao.rodar(async () => {
      const r = await api.post<{ dispositivos: { gravados: number } }>('/api/sync');
      carga.recarregar();
      pend.recarregar();
      return `Sincronizado: ${r.dispositivos.gravados} dispositivo(s).`;
    });
  };

  const personalizar = (): void => {
    void acao.rodar(async () => {
      const p = carga.dados?.painel;
      if (p === undefined) return null;
      await api.post('/api/paineis', {
        nome: 'Meu painel',
        descricao: 'Criado a partir do painel padrão.',
        cartoes: p.cartoes,
      });
      setSalvo(true);
      carga.recarregar();
      return 'Painel copiado para a sua conta. Agora você pode editar os cartões.';
    });
  };

  const porId = new Map((carga.dados?.dados ?? []).map((d) => [d.cartaoId, d]));

  /**
   * A tela se atualiza sozinha enquanto está à vista.
   *
   * O sync já roda de hora em hora por conta própria — o que faltava era a
   * tela acompanhar. Sem isto, alguém deixava a aba aberta a manhã inteira
   * olhando números da noite anterior, sem nada indicando que envelheceram.
   */
  useAtualizacaoPeriodica(() => {
    carga.recarregar();
    pend.recarregar();
  }, 60);

  /**
   * Move um cartão de posição.
   *
   * A ordem **é** a posição no vetor `cartoes` — não existe campo `ordem` e não
   * precisa existir. Menos um campo para manter em sincronia, e reordenar vira
   * uma gravação só.
   */
  const moverCartao = (de: number, para: number): void => {
    const p = carga.dados?.painel;
    if (p === undefined || p.id === null) return;
    if (para < 0 || para >= p.cartoes.length || de === para) return;

    const novos = [...p.cartoes];
    const [movido] = novos.splice(de, 1);
    if (movido === undefined) return;
    novos.splice(para, 0, movido);

    void acao.rodar(async () => {
      await api.put(`/api/paineis/${p.id}`, { cartoes: novos });
      carga.recarregar();
      return null;
    });
  };

  /**
   * Solta o cartão arrastado sobre a posição de destino.
   *
   * 🔴 Separado do `aoMover` de propósito. Cada cartão carrega o próprio índice
   * de origem; usar o índice do cartão **de destino** como origem produzia
   * `mover(j, j)` — o arrastar não fazia absolutamente nada, e sem erro nenhum
   * para denunciar. Quem arrasta é o que está na mão, não o que está embaixo.
   */
  const soltarSobre = (destino: number): void => {
    const p = carga.dados?.painel;
    if (p === undefined || arrastando === null) return;
    const origem = p.cartoes.findIndex((c) => c.id === arrastando);
    if (origem >= 0) moverCartao(origem, destino);
  };

  /** Grava um cartão editado no lugar do antigo, preservando a ordem. */
  const salvarCartao = (novo: Cartao): void => {
    const p = carga.dados?.painel;
    if (p === undefined || p.id === null) return;
    void acao.rodar(async () => {
      await api.put(`/api/paineis/${p.id}`, {
        cartoes: p.cartoes.map((c) => (c.id === novo.id ? novo : c)),
      });
      setEditando(null);
      carga.recarregar();
      return 'Cartão atualizado.';
    });
  };

  /** Remove um cartão do painel do usuário. O de fábrica não é editável. */
  const removerCartao = (cartaoId: string): void => {
    void acao.rodar(async () => {
      const p = carga.dados?.painel;
      if (p === undefined || p.id === null) {
        throw new Error(
          'Este é o painel padrão. Clique em "Personalizar este painel" para poder editá-lo.',
        );
      }
      await api.put(`/api/paineis/${p.id}`, {
        cartoes: p.cartoes.filter((c) => c.id !== cartaoId),
      });
      carga.recarregar();
      return 'Cartão removido.';
    });
  };

  return (
    <>
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      {/*
        Faixa de confiança.

        O painel inteiro vale o que o último sync vale. Sem isto, alguém decide
        com confiança sobre um retrato de semana passada — que é o pior modo de
        falha de um produto de inventário.
      */}
      {pend.dados !== null && (
        <div className={`faixa-sync${pend.dados.sync.obsoleto ? ' obsoleta' : ''}`}>
          <span>
            {pend.dados.sync.em === null
              ? 'Nunca sincronizado.'
              : `Última sincronização: ${data(pend.dados.sync.em)}` +
                (pend.dados.sync.horasAtras !== null && pend.dados.sync.horasAtras >= 1
                  ? ` · há ${pend.dados.sync.horasAtras}h`
                  : ' · agora há pouco')}
            {pend.dados.sync.obsoleto && ' — os números abaixo podem estar desatualizados.'}
            {pend.dados.sync.automatico === true && (
              <span className="suave">
                {' · '}
                atualiza sozinho a cada{' '}
                {pend.dados.sync.intervaloMin >= 60
                  ? `${Math.round(pend.dados.sync.intervaloMin / 60)}h`
                  : `${pend.dados.sync.intervaloMin}min`}
              </span>
            )}
          </span>
          {/*
            Sem permissão o botão some, mas o aviso de inventário obsoleto fica:
            saber que o dado está velho é útil mesmo para quem não pode
            atualizá-lo — é o que faz a pessoa procurar quem pode.
          */}
          {podeSincronizar && (
            <button onClick={sincronizar} disabled={acao.executando}>
              {acao.executando ? 'Sincronizando…' : 'Sincronizar agora'}
            </button>
          )}
        </div>
      )}

      {/*
        O briefing vem antes dos cartões de propósito.

        Os cartões mostram números que a pessoa precisa interpretar; o briefing
        já chega com o que está pior, ordenado por score determinístico e
        explicado. Colocá-lo depois obrigaria a rolar a tela para descobrir o
        que era urgente — e quem abre a Home de manhã abre para saber
        exatamente isso.
      */}
      {/*
        A anomalia vem primeiro de tudo.
        É a única das três que exige olhar HOJE: o briefing descreve o estado e
        a tendência descreve a direção, mas "isto nunca aconteceu neste parque"
        é o que faz alguém largar o que está fazendo. Quando não há anomalia (ou
        não há base para afirmar), o painel some — não ocupa a primeira dobra da
        tela com um "nada a relatar".
      */}
      {/*
        O Centro vem primeiro e numa requisição só.
        Ele já traz anomalia, problema, recomendação, tendência e fila — a Home
        pedia seis endpoints e se montava por pedaços, cada um aparecendo num
        momento diferente, com o agravante de que uma falha deixava um buraco
        sem explicação.
      */}
      <Centro aoPerderSessao={aoPerderSessao} irPara={(d) => irPara(d)} />

      <Briefing aoPerderSessao={aoPerderSessao} />

      {/*
        A tendência vem logo abaixo do briefing porque as duas respondem
        perguntas encadeadas: "o que está ruim hoje?" e "está piorando?". A
        segunda muda completamente o que fazer com a primeira — 6 máquinas com
        patch crítico que eram 40 na semana passada é uma operação dando certo.
      */}
      <Tendencias aoPerderSessao={aoPerderSessao} />

      {/* Fila de trabalho: o que espera por você, com destino. */}
      {pend.dados !== null && pend.dados.pendencias.length > 0 && (
        <section className="pendencias">
          <h2>O que precisa de você</h2>
          <div className="lista-pendencias">
            {pend.dados.pendencias.map((p) => (
              <button
                key={p.chave}
                className={`pendencia ${p.gravidade}`}
                onClick={() => irPara(p.destino as 'sentinela')}
              >
                <span className="qtd">{p.total}</span>
                <span className="txt">
                  <strong>{p.titulo}</strong>
                  <em>{p.detalhe}</em>
                </span>
                <span className="seta" aria-hidden="true">
                  ›
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <Estado carga={carga}>
        {carga.dados !== null && (
          <>
            <div className="titulo-tela">
              <div>
                <h1>{carga.dados.painel.nome}</h1>
                {carga.dados.painel.descricao !== '' && (
                  <p className="suave">{carga.dados.painel.descricao}</p>
                )}
              </div>
              <div className="linha encolhe" style={{ gap: 8 }}>
                {carga.dados.deFabrica && !salvo && (
                  <button onClick={personalizar} disabled={acao.executando}>
                    Personalizar este painel
                  </button>
                )}
                <button onClick={() => setGerindo((v) => !v)}>
                  {gerindo ? 'Fechar painéis' : 'Meus painéis'}
                </button>
              </div>
            </div>

            {gerindo && (
              <GerenciarPaineis
                aoPerderSessao={aoPerderSessao}
                meuLogin={meuLogin}
                aoFechar={() => setGerindo(false)}
                aoMudar={() => carga.recarregar()}
              />
            )}

            {/*
              O `div` existe só para a rolagem ter onde se ancorar: `EditorCartao`
              não encaminha `ref`, e envolvê-lo mantém a âncora aqui — onde está
              a decisão de rolar — em vez de espalhá-la pelo editor.
            */}
            {editando !== null && (
              <div ref={refEditor}>
                <EditorCartao
                  cartao={editando}
                  irmaos={carga.dados.painel.cartoes}
                  aoFechar={() => setEditando(null)}
                  aoSalvar={salvarCartao}
                  aoPerderSessao={aoPerderSessao}
                />
              </div>
            )}

            {/*
              Indicadores prontos.
              Chamado e conformidade não cabem no compilador de consulta — ver
              `apps/app/src/indicadores.ts`. Em vez de esconder isso, o produto
              oferece a lista do que sabe contar.
            */}
            {escolhendoIndicador && (
              <div className="editor-cartao">
                <header>
                  <strong>Acrescentar indicador</strong>
                  <button
                    className="icone"
                    onClick={() => setEscolhendoIndicador(false)}
                    aria-label="Fechar"
                  >
                    ✕
                  </button>
                </header>
                <p className="suave" style={{ fontSize: 13, margin: 0 }}>
                  Números de chamados e conformidade. Diferente dos cartões de pergunta, estes vêm
                  prontos — são os mesmos que alimentam as telas de origem, então nunca discordam
                  delas.
                </p>
                <div className="sugestoes-indicador">
                  {(catalogo.dados?.itens ?? []).map((d) => (
                    <button
                      key={d.chave}
                      disabled={acao.executando}
                      onClick={() => acrescentarIndicador(d)}
                    >
                      <strong>{d.titulo}</strong>
                      <span className="suave">{d.nota}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grade-painel">
              {carga.dados.painel.cartoes.map((c, i) => (
                <CartaoPainel
                  key={c.id}
                  cartao={c}
                  dados={porId.get(c.id) ?? null}
                  aoAbrir={() =>
                    aoAbrirNoInventario({
                      titulo: c.titulo,
                      explicacao: c.consulta.explicacao ?? '',
                      consulta: c.consulta,
                    })
                  }
                  aoEscolherGrupo={(chave) =>
                    aoAbrirNoInventario({
                      titulo: `${c.titulo} · ${chave}`,
                      explicacao: `${c.consulta.explicacao ?? ''} — só ${chave}`,
                      consulta: comFiltroDeGrupo(c.consulta, chave),
                    })
                  }
                  {...(carga.dados?.deFabrica === false
                    ? {
                        aoRemover: () => removerCartao(c.id),
                        aoEditar: () => setEditando(c),
                        posicao: i,
                        total: carga.dados.painel.cartoes.length,
                        aoMover: (para: number) => moverCartao(i, para),
                        aoSoltar: soltarSobre,
                        arrastando,
                        setArrastando,
                      }
                    : {})}
                />
              ))}
            </div>

            {carga.dados.painel.cartoes.length === 0 && (
              <Aviso>
                Este painel não tem cartões. Vá em <strong>Perguntar</strong>, faça uma pergunta
                sobre o inventário e salve a resposta como cartão.
              </Aviso>
            )}

            <p className="suave rodape-painel">
              Os números vêm do inventário já sincronizado — são tão recentes quanto o último sync.{' '}
              <button className="link" onClick={() => irPara('chat')}>
                Criar um cartão perguntando
              </button>
              {!carga.dados.deFabrica && (
                <>
                  {' · '}
                  <button className="link" onClick={() => setEscolhendoIndicador((v) => !v)}>
                    Acrescentar indicador pronto
                  </button>
                </>
              )}
            </p>
          </>
        )}
      </Estado>
    </>
  );
}
